import { useState, useEffect, FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../components/common/layout/Header";
import Footer from "../../components/common/layout/Footer";
import { GradientButton } from "../../components/common/ui/Button";
import { getCartData, calculateTotal, calculateDomainTotal, addServiceToCart, removeServiceFromCart, updateDomainPeriod, CartData } from "@/utils/cart";
import { getLinuxHostingPlans, getWordPressHostingPlans, getWindowsHostingPlans, registerDomain, getCustomerDetails, createCustomerLoginLink } from "@/services/dreamscapeApi";
import { redirectToPayment, getPaymentStatusFromUrl, verifyPaymentStatus } from "@/services/paymentService";
import { API_URL } from "@/config/api";
import {
  ShoppingCart, CreditCard, CheckCircle2, AlertCircle,
  Loader2, Plus, Minus, Globe, Server, LayoutDashboard, User
} from "lucide-react";
import { AnimatedButton } from "../../components/common/animations";
import { getSession } from "@/utils/session";
import { convertCurrency, convertToUSD, formatPriceWithCurrency, CurrencyCode, currencies as availableCurrencies } from "@/utils/currencyConverter";
import { isValidABN, isValidACN } from "@/utils/validation";

interface ServicePlan {
  id: number;
  name: string;
  type: string;
  price: number;
  currency: string;
  period?: number;
}

export default function Checkout() {
  const navigate = useNavigate();
  const [cartData, setCartData] = useState<CartData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingServices, setLoadingServices] = useState<boolean>(false);
  const [step, setStep] = useState<number>(1); // 1: Review, 2: Payment, 3: Success
  const [error, setError] = useState<string | null>(null);
  const [selectedCurrency, setSelectedCurrencyState] = useState<CurrencyCode>(
    (localStorage.getItem('preferredCurrency') as CurrencyCode) || 'USD'
  );

  const setSelectedCurrency = (currency: CurrencyCode) => {
    setSelectedCurrencyState(currency);
    localStorage.setItem('preferredCurrency', currency);
  };

  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };
  const [availableServices, setAvailableServices] = useState<ServicePlan[]>([]);
  const session = getSession();
  const [customerId, setCustomerId] = useState<number | null>(session?.customerId || null);
  const [customer, setCustomer] = useState<any>(null);
  const [purchasedDomain, setPurchasedDomain] = useState<string | null>(null);
  const [redirectCountdown, setRedirectCountdown] = useState<number>(5);
  const [isDummyOrder, setIsDummyOrder] = useState<boolean>(false);
  const [dummyInvoice, setDummyInvoice] = useState<any>(null);

  useEffect(() => {
    // Check if user is logged in
    if (!session || !session.customerId) {
      navigate("/login?redirect=/checkout");
      return;
    }

    setCustomerId(session.customerId);

    // Fetch customer details to show registrant info
    getCustomerDetails(session.customerId).then(res => {
      if (res.success && res.customer) {
        setCustomer(res.customer);
        // Set default currency based on country if no manual preference exists
        const hasManualPref = localStorage.getItem('preferredCurrency');
        if (!hasManualPref) {
          const country = (res.customer.country || res.customer.country_code || '').toUpperCase();
          if (country === 'NP' || country === 'NEPAL') {
            setSelectedCurrency('NRS');
          } else if (country === 'AU' || country === 'AUSTRALIA') {
            setSelectedCurrency('AUD');
          } else {
            setSelectedCurrency('USD');
          }
        }
      }
    }).catch(err => console.error("Error fetching customer:", err));

    // Load cart data
    const cart = getCartData();
    if (!cart || (!cart.items.domains && !cart.items.domain)) {
      navigate("/domains");
      return;
    }
    setCartData(cart);

    // Load available services
    loadAvailableServices(cart);
  }, [navigate]);

  useEffect(() => {
    // Check for payment callback from storefront
    const paymentStatus = getPaymentStatusFromUrl();
    if (paymentStatus.success && paymentStatus.orderId) {
      // Payment was successful, verify and show success
      (async () => {
        setLoading(true);
        try {
          const verification = await verifyPaymentStatus(paymentStatus.orderId!);
          if (verification.success) {
            // Clear cart
            localStorage.removeItem("cartData");

            // Get domain name from cart or order
            const cart = getCartData();
            if (cart?.items.domain) {
              setPurchasedDomain(cart.items.domain.name);
            }

            // Create login link
            if (session && session.customerId) {
              try {
                const loginLinkResult = await createCustomerLoginLink(session.customerId);
                if (loginLinkResult.success && loginLinkResult.loginLink) {
                  // Link logic if needed
                }
              } catch (linkError) {
                console.error("Error creating login link:", linkError);
              }
            }

            setStep(3);
            setLoading(false);

            // Clean URL
            window.history.replaceState({}, document.title, '/checkout');
          } else {
            showToast(verification.message || "Payment verification failed", "error");
            setLoading(false);
          }
        } catch (error) {
          showToast("Failed to verify payment status", "error");
          setLoading(false);
        }
      })();
      return;
    } else if (paymentStatus.error) {
      // Payment failed or was cancelled
      showToast(paymentStatus.error || "Payment was cancelled or failed", "error");
      window.history.replaceState({}, document.title, '/checkout');
      return;
    }
  }, [navigate, session]);

  // Auto-redirect to user dashboard after 5 seconds on success screen
  useEffect(() => {
    if (step === 3) {
      setRedirectCountdown(5);
      const countdownInterval = setInterval(() => {
        setRedirectCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            navigate('/profile');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(countdownInterval);
    }
  }, [step, navigate]);

  const loadAvailableServices = async (currentCart?: CartData | null) => {
    const activeCart = currentCart || cartData;

    setLoadingServices(true);
    try {
      const [linuxHosting, wordpressHosting, windowsHosting] = await Promise.all([
        getLinuxHostingPlans({ limit: 5 }),
        getWordPressHostingPlans({ limit: 5 }),
        getWindowsHostingPlans({ limit: 5 }),
      ]);

      const services: ServicePlan[] = [];

      // Helper to safely convert price to number
      const parsePrice = (price: any): number => {
        if (price === null || price === undefined) return 0;
        if (typeof price === 'number') return price;
        const parsed = parseFloat(String(price));
        return isNaN(parsed) ? 0 : parsed;
      };

      // Process Linux Hosting plans
      if (linuxHosting.success && linuxHosting.plans) {
        linuxHosting.plans.forEach((plan: any) => {
          const period = plan.periods?.[0];
          if (period && period.price) {
            const price = parsePrice(period.price.register || period.price.renew);
            if (price > 0) {
              services.push({
                id: plan.id,
                name: plan.name || "Linux Hosting",
                type: "linux-hosting",
                price: price,
                currency: activeCart?.items.domain?.currency || "USD",
                period: period.period || 1,
              });
            }
          }
        });
      }

      // Process WordPress Hosting plans
      if (wordpressHosting.success && wordpressHosting.plans) {
        wordpressHosting.plans.forEach((plan: any) => {
          const period = plan.periods?.[0];
          if (period && period.price) {
            const price = parsePrice(period.price.register || period.price.renew);
            if (price > 0) {
              services.push({
                id: plan.id,
                name: plan.name || "WordPress Hosting",
                type: "wordpress-hosting",
                price: price,
                currency: activeCart?.items.domain?.currency || "USD",
                period: period.period || 1,
              });
            }
          }
        });
      }

      // Process Windows Hosting plans
      if (windowsHosting.success && windowsHosting.plans) {
        windowsHosting.plans.forEach((plan: any) => {
          const period = plan.periods?.[0];
          if (period && period.price) {
            const price = parsePrice(period.price.register || period.price.renew);
            if (price > 0) {
              services.push({
                id: plan.id,
                name: plan.name || "Windows Hosting",
                type: "windows-hosting",
                price: price,
                currency: activeCart?.items.domain?.currency || "USD",
                period: period.period || 1,
              });
            }
          }
        });
      }

      setAvailableServices(services);
    } catch (err) {
      console.error("Error loading services:", err);
      // Continue without services if API fails
    } finally {
      setLoadingServices(false);
    }
  };

  const isServiceInCart = (serviceType: string, planId: number): boolean => {
    if (!cartData?.items.services) return false;
    return cartData.items.services.some(
      s => s.type === serviceType && s.planId === planId
    );
  };

  const handleToggleService = (service: ServicePlan) => {
    if (!cartData) return;

    const isInCart = isServiceInCart(service.type, service.id);

    if (isInCart) {
      removeServiceFromCart(service.type, service.id);
    } else {
      // Ensure price is a number
      const price = typeof service.price === 'number'
        ? service.price
        : parseFloat(String(service.price)) || 0;

      addServiceToCart({
        type: service.type,
        name: service.name,
        planId: service.id,
        price: price,
        currency: service.currency,
        period: service.period,
      });
    }

    // Reload cart data
    const updatedCart = getCartData();
    setCartData(updatedCart);
  };

  const handleDomainPeriodChange = (domainName: string, period: number) => {
    if (!cartData) return;
    updateDomainPeriod(domainName, period);
    // Reload cart data to reflect the change
    const updatedCart = getCartData();
    setCartData(updatedCart);
  };

  const handleProceedToPayment = async () => {
    if (!cartData?.items.domains || cartData.items.domains.length === 0) {
      if (!cartData?.items.domain) {
        setError("Domain information is missing");
        return;
      }
    }

    // Validate customer details before proceeding
    if (!customerId) {
      setError("User session expired. Please log in again.");
      return;
    }

    setLoading(true);
    try {
      const res = await getCustomerDetails(customerId);
      if (res.success && res.customer) {
        // The service already normalizes/flatens this into a standard object
        const c = res.customer;
        console.log('📋 Validating Customer details:', {
          id: customerId,
          source: c.is_local_only ? 'Local DB Only' : 'Dreamscape API + Local Cache',
          names: `${c.firstName} ${c.lastName}`,
          keys: Object.keys(c)
        });

        // Deep check for field presence across common naming conventions
        const getFieldVal = (obj: any, camel: string, snake: string) => {
          if (!obj) return null;
          let val = obj[camel] ?? obj[snake] ?? obj[camel.toLowerCase()] ?? obj[snake.toUpperCase()];

          // Handle object values (e.g., country: { name: "Nepal", code: "NP" })
          if (val && typeof val === 'object') {
            val = val.name || val.value || val.label || val.text || JSON.stringify(val);
          }
          return val;
        };

        const hasField = (camel: string, snake: string) => {
          const val = getFieldVal(c, camel, snake);
          const isValid = val !== null && val !== undefined && String(val).trim().length > 0;
          if (!isValid) {
            console.warn(`❌ Checkout Detail Missing: "${camel}"`, { source: c });
          }
          return isValid;
        };

        const required: [string, string, string][] = [
          ['firstName', 'first_name', 'First Name'],
          ['lastName', 'last_name', 'Last Name'],
          ['phone', 'phone_number', 'Phone Number'],
          ['address', 'address_1', 'Address'],
          ['city', 'city', 'City'],
          ['state', 'state', 'State'],
          ['postCode', 'post_code', 'Postal Code'],
          ['country', 'country_code', 'Country'],
        ];

        const missing = required
          .filter(([camel, snake]) => !hasField(camel, snake))
          .map(([, , label]) => label);

        if (missing.length > 0) {
          console.error("⛔ Checkout blocked by missing profile details:", missing);
          showToast(`Required details missing for registration: ${missing.join(', ')}. Please go to Profile and Save all fields.`, "error");
          setLoading(false);
          return;
        }
      } else {
        showToast("Failed to verify your account details. Please ensure your profile is complete.", "error");
        setLoading(false);
        return;
      }
    } catch (err) {
      showToast("Verification error. Please try again.", "error");
    } finally {
      setLoading(false);
    }

    setStep(2);
  };

  const handlePayment = async (e: FormEvent) => {
    e.preventDefault();

    const domainsToRegister = cartData?.items.domains && cartData.items.domains.length > 0
      ? cartData.items.domains
      : (cartData?.items.domain ? [cartData.items.domain] : []);

    if (domainsToRegister.length === 0 || !customerId) {
      setError("Missing required information");
      return;
    }

    setLoading(true);
    setError(null);

    // Normal payment flow
    try {
      // Get customer details from API
      const customerResult = await getCustomerDetails(customerId);

      if (!customerResult.success || !customerResult.customer) {
        setError("Failed to retrieve customer information. Please try again.");
        setLoading(false);
        return;
      }

      const customer = customerResult.customer;
      // Note: customerEmail in localStorage is deprecated, we pull from session/API now


      // Prepare contact info from customer data
      const contactInfo = {
        firstName: customer.firstName || customer.first_name || "",
        lastName: customer.lastName || customer.last_name || "",
        email: customer.email || "",
        phone: customer.phone || "",
        address: customer.address || "",
        city: customer.city || "",
        state: customer.state || "",
        zip: customer.postCode || customer.post_code || "",
        country: customer.country || (domainsToRegister[0].currency === "USD" ? "US" : "AU"),
        organization: customer.businessName || customer.business_name || undefined,
        accountType: customer.accountType || (customer.businessName ? 'business' : 'personal'),
        businessName: customer.businessName || customer.business_name,
        businessNumberType: customer.businessNumberType || customer.business_number_type,
        businessNumber: customer.businessNumber || customer.business_number
      };

      // Pre-validation: Direct check for business account integrity
      if (contactInfo.accountType === 'business') {
        if (!contactInfo.businessName || !contactInfo.businessNumber) {
          setError("Your professional profile is incomplete. Business account registration requires a Legal Entity Name and ID (ABN/Number). Please click 'Edit Profile' to update your details.");
          setLoading(false);
          return;
        }

        // Check ABN/ACN checksum if applicable
        if (contactInfo.businessNumberType === 'ABN' && !isValidABN(contactInfo.businessNumber)) {
          setError("Your Australian Business Number (ABN) is invalid. Please update your profile with a valid 11-digit ABN.");
          setLoading(false);
          return;
        }
        if (contactInfo.businessNumberType === 'ACN' && !isValidACN(contactInfo.businessNumber)) {
          setError("Your Australian Company Number (ACN) is invalid. Please update your profile with a valid 9-digit ACN.");
          setLoading(false);
          return;
        }
      }

      let firstResult = null;

      // Register all domains
      for (const domain of domainsToRegister) {
        const result = await registerDomain(
          domain.name,
          contactInfo,
          {
            period: domain.period || 1,
            privacy: domain.privacy,
            customerId: customerId,
          }
        );
        if (!firstResult) firstResult = result;
      }

      const result = firstResult;

      if (result.success) {
        // Clear cart
        localStorage.removeItem("cartData");

        // Store purchased domain for success screen
        setPurchasedDomain(domainsToRegister[0].name);

        // Check if this was a dummy/test order
        if (result.isDummy) {
          setIsDummyOrder(true);
          setDummyInvoice(result.dummyInvoice || null);
        }

        // Show success screen
        setStep(3);
        setLoading(false);
      } else if (result.isPaymentError) {
        // Check if payment method is disabled via environment variable
        const skipPaymentMethod = import.meta.env.VITE_DREAMSCAPE_SKIP_PAYMENT_METHOD === 'true';

        if (skipPaymentMethod) {
          // If payment mode is skipped, silently succeed out of the 402 loop via dummy endpoint
          window.location.href = `${window.location.origin}/checkout?success=true&order_id=${result.orderId || result.invoiceId || domainsToRegister[0].name}&dummy=true`;
          return;
        }

        // Payment is required - redirect to storefront payment page
        const total = calculateTotal(cartData);
        const currency = domainsToRegister[0]?.currency || "USD";

        // Redirect to invoice page which handles secure payment routing
        if (result.invoiceId) {
          window.location.href = `${API_URL}/domain/invoice/${result.invoiceId}/download`;
        } else {
          redirectToPayment({
            orderId: result.orderId || domainsToRegister[0].name,
            customerId: customerId,
            amount: total,
            currency: currency,
            domainName: domainsToRegister[0].name,
            returnUrl: `${window.location.origin}/checkout?success=true&order_id=${result.orderId || domainsToRegister[0].name}`,
            cancelUrl: `${window.location.origin}/checkout?error=Payment cancelled`,
          });
        }

        // Set loading state while redirecting
        setLoading(true);
      } else {
        const msg = result.message || "";
        if (msg.toLowerCase().includes("registrant") || msg.toLowerCase().includes("business")) {
          showToast(`Registry Validation Error: ${msg}. Please ensure your account details are correct.`, "error");
        } else {
          showToast(msg || "Registration failed. Please review your details and try again.", "error");
        }
        setLoading(false);
      }
    } catch (err) {
      console.error("Payment error:", err);
      showToast(err instanceof Error ? err.message : "An error occurred during payment.", "error");
      setLoading(false);
    }
  };

  if (!cartData) {
    return (
      <div className="min-h-screen text-white relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#000000e0] via-[#1a1a3e] to-[#000000e0] opacity-50" />
        </div>
        <Header />
        <div className="container py-12 text-center relative z-10">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-white">Loading cart...</p>
        </div>
        <Footer />
      </div>
    );
  }

  // Enhanced Currency Logic
  const domainsList = cartData.items.domains || (cartData.items.domain ? [cartData.items.domain] : []);
  const cartBaseCurrency = (domainsList[0]?.currency as any) || "USD";
  const total = calculateTotal(cartData);

  const formatPrice = (price: any, sourceCurrency: string = "USD"): string => {
    const numPrice = typeof price === 'number' ? price : parseFloat(String(price));
    if (isNaN(numPrice)) return '0.00';
    try {
      // Use convertToUSD and convertCurrency for accurate cross-conversion
      const priceInUSD = convertToUSD(numPrice, sourceCurrency as any);
      return formatPriceWithCurrency(convertCurrency(priceInUSD, selectedCurrency as any), selectedCurrency as any);
    } catch (e) {
      return formatPriceWithCurrency(numPrice, selectedCurrency as any);
    }
  };

  return (
    <div className="min-h-screen text-white bg-[#030308] relative overflow-hidden">
      {/* Background effects enhanced for crazy aesthetic */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-[-5%] right-[5%] w-[600px] h-[600px] bg-cyan-600/10 blur-[150px] rounded-full animate-float-slow" />
        <div className="absolute bottom-[10%] left-[-5%] w-[500px] h-[500px] bg-blue-700/10 blur-[150px] rounded-full animate-pulse" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#030308]/20 via-transparent to-[#030308]" />
        {/* Subtle pattern */}
        <div className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.05) 1px, transparent 0)',
            backgroundSize: '30px 30px'
          }}
        />
      </div>

      <Header />

      <div className="container py-12 px-4 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* NEW: Phase Selection Tabs (Tabular Selection) */}
          <div className="flex mb-10 bg-white/5 p-1 rounded-2xl border border-white/10 max-w-2xl mx-auto shadow-2xl relative z-20 backdrop-blur-md">
            <button
              onClick={() => setStep(1)}
              className={`flex-1 py-4 px-6 rounded-xl transition-all duration-500 flex items-center justify-center gap-3 group ${step === 1 ? 'bg-gradient-to-r from-cyan-600 to-blue-600 shadow-lg shadow-cyan-500/20' : 'text-white/40 hover:text-white hover:bg-white/5'}`}
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-black ${step === 1 ? 'bg-white/20 text-white' : 'bg-white/5 text-white/20'}`}>1</div>
              <span className={`text-sm font-black uppercase tracking-widest ${step === 1 ? 'text-white' : 'text-white/40 group-hover:text-white'}`}>Review Purchase</span>
            </button>
            <button
              onClick={() => !loading && cartData && setStep(2)}
              disabled={step === 3 || loading || !cartData}
              className={`flex-1 py-4 px-6 rounded-xl transition-all duration-500 flex items-center justify-center gap-3 group ${step === 2 ? 'bg-gradient-to-r from-cyan-600 to-blue-600 shadow-lg shadow-cyan-500/20' : step === 3 ? 'opacity-50 cursor-not-allowed' : 'text-white/40 hover:text-white hover:bg-white/5'}`}
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-black ${step === 2 ? 'bg-white/20 text-white' : 'bg-white/5 text-white/20'}`}>2</div>
              <span className={`text-sm font-black uppercase tracking-widest ${step === 2 ? 'text-white' : 'text-white/40 group-hover:text-white'}`}>Payment Selection</span>
            </button>
          </div>

          {step === 1 ? (
            <div className="grid md:grid-cols-3 gap-8">
              {/* Cart Items */}
              <div className="md:col-span-2 space-y-6">

                {/* Registrant Details section */}
                {customer && (
                  <div className="bg-white/[0.03] backdrop-blur-2xl rounded-3xl p-6 border border-white/10 shadow-2xl relative overflow-hidden group hover:border-white/20 transition-all duration-500">
                    <div className="absolute top-[-50px] right-[-50px] w-48 h-48 bg-cyan-500/10 rounded-full blur-[40px] group-hover:bg-cyan-500/20 transition-all duration-700"></div>

                    <div className="flex items-center justify-between mb-6 relative z-10 border-b border-white/5 pb-4">
                      <h2 className="text-xl font-bold flex items-center gap-3 text-white">
                        <div className="w-10 h-10 bg-white/5 rounded-xl flex items-center justify-center border border-white/10 shadow-inner">
                          <User className="w-5 h-5 text-white/70" />
                        </div>
                        <span className="tracking-tight">Registrant Details</span>
                      </h2>
                      <button onClick={() => navigate('/profile')} className="text-[10px] font-black tracking-[0.2em] text-white/40 uppercase hover:text-white transition-colors bg-white/5 px-4 py-2 rounded-lg border border-white/10 hover:bg-white/10 shadow-lg">
                        Edit Profile
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 relative z-10">
                      <div className="space-y-1.5 group/field">
                        <div className="text-[10px] uppercase font-black text-white/60 tracking-widest group-hover/field:text-cyan-400/50 transition-colors">Full Name</div>
                        <div className="text-base font-semibold text-white/90">
                          {customer.firstName || customer.first_name || ''} {customer.lastName || customer.last_name || ''}
                          {!(customer.firstName || customer.first_name) && <span className="text-orange-400 text-sm">Missing Name</span>}
                        </div>
                      </div>

                      <div className="space-y-1.5 group/field">
                        <div className="text-[10px] uppercase font-black text-white/60 tracking-widest group-hover/field:text-cyan-400/50 transition-colors">Email Address</div>
                        <div className="text-base font-semibold text-white/90">
                          {customer.email || <span className="text-orange-400 text-sm">Missing Email</span>}
                        </div>
                      </div>

                      <div className="space-y-1.5 group/field">
                        <div className="text-[10px] uppercase font-black text-white/60 tracking-widest group-hover/field:text-cyan-400/50 transition-colors">Phone Number</div>
                        <div className="text-base font-semibold text-white/90">
                          {customer.phone || customer.phone_number || customer.telephone || <span className="text-orange-400 text-sm">Missing Phone</span>}
                        </div>
                      </div>

                      <div className="space-y-1.5 group/field">
                        <div className="text-[10px] uppercase font-black text-white/60 tracking-widest group-hover/field:text-cyan-400/50 transition-colors">Billing Location</div>
                        <div className="text-base font-semibold text-white/90">
                          {[customer.address || customer.street_address, customer.city, customer.state || customer.province, customer.country || customer.country_name].filter(Boolean).join(', ') || <span className="text-orange-400 text-sm">Missing Address Details</span>}
                        </div>
                      </div>

                      <div className="space-y-1.5 group/field">
                        <div className="text-[10px] uppercase font-black text-white/60 tracking-widest group-hover/field:text-cyan-400/50 transition-colors">Account Type</div>
                        <div className="text-base font-semibold text-white/90 capitalize">
                          {customer.accountType || (customer.businessName ? 'Business' : 'Personal')}
                        </div>
                      </div>

                      {(customer.businessName || (customer as any).business_name) && (
                        <>
                          <div className="space-y-1.5 group/field">
                            <div className="text-[10px] uppercase font-black text-white/60 tracking-widest group-hover/field:text-cyan-400/50 transition-colors">Business Name</div>
                            <div className="text-base font-semibold text-white/90">
                              {customer.businessName || (customer as any).business_name}
                            </div>
                          </div>

                          <div className="space-y-1.5 group/field">
                            <div className="text-[10px] uppercase font-black text-white/60 tracking-widest group-hover/field:text-cyan-400/50 transition-colors">
                              {(customer as any).businessNumberType || (customer as any).business_number_type || 'ID Number'}
                            </div>
                            <div className="text-base font-semibold text-white/90">
                              {(customer as any).businessNumber || (customer as any).business_number}
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    <div className="mt-6 pt-4 border-t border-cyan-500/10 flex items-start gap-3">
                      <AlertCircle className="w-4 h-4 text-cyan-500/50 flex-shrink-0 mt-0.5" />
                      <p className="text-[10px] text-white/60 leading-relaxed max-w-[90%]">
                        These details will be used as the legal registrant and billing contact for all domains in your cart. Valid information is required by ICANN.
                      </p>
                    </div>
                  </div>
                )}

                {/* Domains */}
                {domainsList.map((dom, idx) => (
                  <div key={idx} className="bg-white/[0.03] backdrop-blur-2xl rounded-3xl p-6 border border-white/10 shadow-2xl hover:border-white/20 transition-all duration-500">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-white/5 rounded-2xl flex items-center justify-center border border-white/10 shadow-lg">
                          <Globe className="w-6 h-6 text-white/70" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-white">{dom.name}</h3>
                          <p className="text-white text-sm">
                            Domain Registration
                          </p>
                          {dom.privacy && (
                            <span className="inline-block mt-1 px-2 py-1 bg-primary/20 text-white text-xs rounded">
                              Privacy Protection
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col md:flex-row md:items-center gap-4">
                        <div className="flex flex-col gap-2">
                          <label className="text-sm text-white">Registration Period</label>
                          <select
                            value={dom.period || (dom.name.toLowerCase().endsWith('.com.au') ? 3 : 1)}
                            onChange={(e) => handleDomainPeriodChange(dom.name, parseInt(e.target.value))}
                            className={`px-4 py-2 bg-[#000000e0]/50 border border-primary/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary/50 transition-all ${dom.name.toLowerCase().endsWith('.com.au') && dom.period === 3 ? 'border-[#0ff] shadow-[0_0_10px_#0ff] text-[#0ff]' : ''}`}
                          >
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((year) => (
                              <option key={year} value={year} style={{ color: 'black' }}>
                                {year} {year === 1 ? "Year" : "Years"} {dom.name.toLowerCase().endsWith('.com.au') && year === 3 ? "(1st Year $0.05)" : ""}
                              </option>
                            ))}
                          </select>
                          {dom.name.toLowerCase().endsWith('.com.au') && dom.period === 3 && (
                            <span className="text-[10px] text-[#0ff] font-bold uppercase tracking-widest mt-1 drop-shadow-[0_0_5px_rgba(0,255,255,0.8)]">Special 3-Year Offer Applied</span>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-white">
                            {formatPrice(calculateDomainTotal(dom), dom.currency)}
                          </div>
                          <div className="text-sm text-white/60">
                            {(() => {
                              const displayPrice = (dom.name.toLowerCase().endsWith('.com.au') && dom.price < 1) ? 12.59 : dom.price;
                              return dom.name.toLowerCase().endsWith('.com.au') && dom.period === 3
                                ? `1st yr $0.05 + 2 yrs @ ${formatPrice(displayPrice, dom.currency)}`
                                : `${formatPrice(displayPrice, dom.currency)} × ${dom.period || 1} ${dom.period === 1 ? 'year' : 'years'}`;
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Available Services */}
                <div className="bg-white/[0.03] backdrop-blur-2xl rounded-3xl p-6 border border-white/10 shadow-2xl">
                  <h2 className="text-2xl font-bold mb-4 flex items-center gap-2 text-white">
                    <div className="w-10 h-10 bg-white/5 rounded-xl flex items-center justify-center border border-white/10 shadow-lg">
                      <Server className="w-5 h-5 text-white/70" />
                    </div>
                    Add Services
                  </h2>

                  {loadingServices ? (
                    <div className="text-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                      <p className="text-white">Loading services...</p>
                    </div>
                  ) : availableServices.length > 0 ? (
                    <div className="space-y-3">
                      {availableServices.map((service) => {
                        const isInCart = isServiceInCart(service.type, service.id);
                        return (
                          <div
                            key={`${service.type}-${service.id}`}
                            className={`p-4 rounded-xl border transition-all ${isInCart
                              ? "bg-white/10 border-white/20 shadow-md"
                              : "bg-white/[0.02] border-white/5 hover:border-white/10"
                              }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <h4 className="font-semibold text-white">{service.name}</h4>
                                <p className="text-sm text-white">
                                  {service.period} month(s)
                                </p>
                              </div>
                              <div className="flex items-center gap-4">
                                <div className="text-right">
                                  <div className="font-bold text-white">
                                    {formatPrice(service.price, service.currency)}
                                  </div>
                                  <div className="text-xs text-white/60">per period</div>
                                </div>
                                <AnimatedButton
                                  onClick={() => handleToggleService(service)}
                                  variant={isInCart ? "primary" : "secondary"}
                                  className={`w-8 h-8 rounded-lg !px-0 !py-0`}
                                >
                                  {isInCart ? (
                                    <Minus className="w-4 h-4" />
                                  ) : (
                                    <Plus className="w-4 h-4" />
                                  )}
                                </AnimatedButton>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-white text-center py-4">
                      No additional services available at this time.
                    </p>
                  )}
                </div>

                {/* Selected Services */}
                {cartData.items.services && cartData.items.services.length > 0 && (
                  <div className="bg-white/[0.03] backdrop-blur-2xl rounded-3xl p-6 border border-white/10 shadow-2xl">
                    <h2 className="text-xl font-bold mb-4 text-white">Selected Services</h2>
                    <div className="space-y-3">
                      {cartData.items.services.map((service, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 bg-[#000000e0]/50 border border-primary/30 rounded-lg hover:border-primary/50 transition-all"
                        >
                          <div>
                            <h4 className="font-semibold text-white">{service.name}</h4>
                            <p className="text-sm text-white">{service.type}</p>
                          </div>
                          <div className="text-right">
                            <div className="font-bold text-white">
                              {service.currency} ${formatPrice(service.price)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Order Summary - Glassmorphism Enhanced */}
              <div className="md:col-span-1">
                <div className="sticky top-24 space-y-4">
                  {/* Payment/Currency Swapper - Repositioned above summary */}
                  <div className="bg-white/[0.03] backdrop-blur-2xl rounded-2xl p-2 border border-white/10 flex items-center justify-between shadow-xl">
                    <span className="text-[10px] font-black uppercase tracking-widest text-white/40 ml-2">Display Currency</span>
                    <div className="flex bg-white/5 p-1 rounded-xl border border-white/5 shrink-0">
                      {availableCurrencies.map((c) => (
                        <button
                          key={c.code}
                          onClick={() => setSelectedCurrency(c.code)}
                          className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all duration-300 ${selectedCurrency === c.code
                            ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-lg'
                            : 'text-white/40 hover:text-white/60 hover:bg-white/5'}`}
                        >
                          {c.code}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="bg-white/[0.03] backdrop-blur-3xl rounded-3xl p-8 border border-white/10 shadow-2xl">
                    <div className="flex items-center gap-4 mb-8">
                      <div className="w-12 h-12 bg-white/5 rounded-2xl flex items-center justify-center border border-white/10 shadow-lg">
                        <ShoppingCart className="w-6 h-6 text-cyan-400" />
                      </div>
                      <h2 className="text-2xl font-black text-white tracking-tight">Summary</h2>
                    </div>

                    <div className="space-y-4 mb-6">
                      {domainsList.map((dom, idx) => (
                        <div key={idx} className="flex justify-between text-white group/item">
                          <span className="text-white/80 group-hover/item:text-white transition-colors">
                            {dom.name} ({dom.period || 1} {dom.period === 1 ? 'year' : 'years'})
                          </span>
                          <span className="font-bold text-white">
                            {formatPrice(calculateDomainTotal(dom), dom.currency)}
                          </span>
                        </div>
                      ))}

                      {cartData.items.services?.map((service, index) => (
                        <div key={index} className="flex justify-between text-white group/item">
                          <span className="text-white/80 group-hover/item:text-white transition-colors">{service.name}</span>
                          <span className="font-bold text-white">
                            {formatPrice(service.price, service.currency)}
                          </span>
                        </div>
                      ))}

                      <div className="border-t border-white/10 pt-6 mt-6">
                        <div className="flex justify-between items-center text-xl font-black tracking-tight">
                          <span className="text-white">Total</span>
                          <span className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">
                            {formatPrice(total, cartBaseCurrency)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {error && (
                      <div className="mt-6 flex items-center gap-3 p-4 bg-red-400/10 border border-red-400/20 rounded-2xl text-red-400">
                        <AlertCircle className="w-5 h-5 shrink-0" />
                        <p className="text-xs font-bold uppercase tracking-wider">{error}</p>
                      </div>
                    )}

                    <GradientButton
                      variant="dashboard"
                      onClick={handleProceedToPayment}
                      className="w-full py-4 mt-8 font-black uppercase tracking-widest shadow-xl shadow-cyan-500/10 hover:shadow-cyan-500/20 transition-all duration-300 active:scale-[0.98]"
                    >
                      Proceed to Payment
                    </GradientButton>
                  </div>
                </div>
              </div>
            </div>
          ) : step === 2 ? (
            <div className="max-w-2xl mx-auto">
              <div className="bg-white/[0.03] backdrop-blur-3xl rounded-3xl p-8 border border-white/10 shadow-2xl">
                <h2 className="text-3xl font-black mb-8 flex items-center gap-4 text-white">
                  <div className="w-12 h-12 bg-white/5 rounded-2xl flex items-center justify-center border border-white/10 shadow-lg">
                    <CreditCard className="w-6 h-6 text-white/70" />
                  </div>
                  {import.meta.env.VITE_DREAMSCAPE_SKIP_PAYMENT_METHOD === 'true' ? 'Complete Order' : 'Payment'}
                </h2>

                <form onSubmit={handlePayment} className="space-y-6">
                  <div className="bg-white/[0.02] rounded-2xl p-6 border border-white/10">
                    <h3 className="text-[10px] uppercase font-black text-white/40 tracking-widest mb-3">Order Total</h3>
                    <div className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-white to-white/60">
                      {formatPrice(total, cartBaseCurrency)}
                    </div>
                  </div>

                  {import.meta.env.VITE_DREAMSCAPE_SKIP_PAYMENT_METHOD === 'true' ? (
                    <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-2xl p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <CheckCircle2 className="w-6 h-6 text-primary" />
                        <h3 className="text-lg font-semibold text-white">Payment Skipped</h3>
                      </div>
                      <p className="text-white text-sm">
                        Payment processing is disabled. Click "Complete Purchase" to register your domain and access the admin panel.
                      </p>
                    </div>
                  ) : (
                    <div className="bg-white/[0.02] border border-white/10 rounded-3xl p-8 relative overflow-hidden group">
                      <div className="absolute top-[-50px] right-[-50px] w-32 h-32 bg-white/5 rounded-full blur-[40px] group-hover:bg-white/10 transition-all duration-700"></div>
                      <div className="flex items-center gap-4 mb-6 relative z-10">
                        <div className="w-12 h-12 bg-white/5 rounded-xl flex items-center justify-center border border-white/10">
                          <CheckCircle2 className="w-6 h-6 text-emerald-400/70" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-white">Secure Payment Gateway</h3>
                          <p className="text-white/40 text-sm mt-1 tracking-tight">You will be redirected to complete your purchase safely.</p>
                        </div>
                      </div>
                      <div className="space-y-4 relative z-10 border-t border-white/10 pt-6">
                        <div className="flex items-center gap-3">
                          <div className="w-2 h-2 rounded-full bg-cyan-400"></div>
                          <p className="text-white/80 text-sm">Domain reservations are generated instantly.</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-2 h-2 rounded-full bg-cyan-400"></div>
                          <p className="text-white/80 text-sm">Processed via encrypted reseller token.</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-2 h-2 rounded-full bg-cyan-400"></div>
                          <p className="text-white/80 text-sm">Official ICANN compliant invoice generated post-checkout.</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-4">
                    <GradientButton
                      variant="dashboard"
                      type="button"
                      onClick={() => setStep(1)}
                      className="flex-1 py-3 transition-all"
                    >
                      Back
                    </GradientButton>
                    <GradientButton
                      variant="dashboard"
                      type="submit"
                      disabled={loading}
                      className="flex-1 py-3 font-semibold shadow-lg transition-all"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin inline" />
                          Processing...
                        </>
                      ) : (
                        "Complete Purchase"
                      )}
                    </GradientButton>
                  </div>
                </form>
              </div>
            </div>
          ) : (
            // Success Screen
            <div className="max-w-2xl mx-auto">
              <div className="bg-white/[0.03] backdrop-blur-3xl rounded-3xl p-10 border border-white/10 shadow-2xl text-center relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-500" />
                <div className="mb-8 flex justify-center">
                  <div className="w-20 h-20 bg-white/5 rounded-full flex items-center justify-center border border-white/10 shadow-2xl">
                    <CheckCircle2 className="w-10 h-10 text-emerald-400" />
                  </div>
                </div>

                <h2 className="text-4xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-brandStart to-brandEnd">
                  Domain Securely Purchased!
                </h2>

                {isDummyOrder && (
                  <div className="mb-6 inline-flex items-center gap-2 bg-amber-500/20 border border-amber-500/40 text-amber-400 text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full">
                    <AlertCircle className="w-4 h-4" />
                    Dummy Test — No Real Payment Processed
                  </div>
                )}

                {purchasedDomain && (
                  <div className="mb-6">
                    <p className="text-white text-lg mb-2">Order completed successfully for:</p>
                    <p className="text-2xl font-bold text-white">{purchasedDomain}</p>
                  </div>
                )}

                <div className="bg-primary/10 border border-primary/30 rounded-lg p-6 mb-8">
                  {dummyInvoice && (
                    <div className="mb-4 bg-white/5 rounded-xl p-4 border border-white/10 text-left">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white/50 text-xs font-bold uppercase tracking-widest">Invoice</span>
                        <span className="text-xs px-2 py-0.5 rounded-full border font-bold uppercase tracking-widest bg-amber-500/10 text-amber-400 border-amber-500/20">Test</span>
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div><span className="text-white/60">ID:</span> <span className="text-white font-mono">{dummyInvoice.id}</span></div>
                        <div><span className="text-white/60">Date:</span> <span className="text-white">{new Date(dummyInvoice.issueDate).toLocaleDateString()}</span></div>
                        <div><span className="text-white/60">Domain:</span> <span className="text-white">{dummyInvoice.domainName}</span></div>
                        <div><span className="text-white/60">Amount:</span> <span className="text-emerald-400 font-bold">${dummyInvoice.amount || '0.00'}</span></div>
                      </div>
                      <p className="text-amber-400/80 text-xs mt-3 italic">{dummyInvoice.note}</p>
                    </div>
                  )}
                  <p className="text-white mb-4">
                    Your domain purchase is complete. You can now manage your domain, set up DNS, and configure email hosting from your dashboard.
                  </p>

                  {redirectCountdown > 0 && (
                    <div className="text-primary font-semibold mb-6 flex items-center justify-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Redirecting to your Domain Dashboard in {redirectCountdown}...
                    </div>
                  )}

                  <div className="flex flex-col gap-4">
                    <GradientButton
                      onClick={() => navigate('/profile')}
                      className="w-full py-4 text-lg font-bold shadow-[0_0_20px_rgba(0,242,255,0.2)] flex items-center justify-center gap-2 transition-all hover:scale-[1.02]"
                    >
                      <LayoutDashboard className="w-5 h-5" /> Open My Dashboard
                    </GradientButton>

                    <GradientButton
                      onClick={() => navigate("/profile")}
                      className="w-full py-3 bg-white/5 border border-white/10 hover:bg-white/10 text-white font-medium"
                    >
                      View Order Details
                    </GradientButton>
                  </div>
                </div>

                <p className="text-white text-sm mb-8 mt-8">
                  You can also access your dashboard anytime from your <button onClick={() => navigate("/profile")} className="text-primary hover:underline font-medium">Profile</button> page.
                </p>

                <GradientButton
                  onClick={() => navigate("/")}
                  className="w-full py-3 bg-white/5 border border-white/10 hover:border-white/20 text-white"
                >
                  Return to Home
                </GradientButton>
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />

      {/* Toast Notification */}
      {toast && (
        <div className={`fixed top-20 right-6 z-[100] px-5 py-3 rounded-2xl shadow-2xl flex items-center gap-3 text-sm font-medium transition-all animate-in slide-in-from-right-4 slide-out-to-right-4 duration-300 ${toast.type === 'success' ? 'bg-green-500/20 border border-green-500/40 text-green-300' : 'bg-red-500/20 border border-red-500/40 text-red-300'}`}>
          {toast.type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {toast.msg}
        </div>
      )}
    </div>
  );
}
